import { ChangeDetectorRef, Component, DestroyRef, effect, inject, Injector, input, OnDestroy, signal, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatProgressBar } from '@angular/material/progress-bar';
import { DateFormatPipe } from '@iot-platform/pipes';
import { TranslateService } from '@ngx-translate/core';
import * as Highcharts from 'highcharts';
import { HighchartsChartComponent } from 'highcharts-angular';
import { isEmpty, orderBy } from 'lodash';
import moment from 'moment';
import { of } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';
import { ChartAxisPoint } from '../../../models/chart-axis-point.model';
import { ChartService } from '../../../services/chart.service';
import { ChartFilterComponent } from '../chart-filter/chart-filter.component';

@Component({
  selector: 'oyan-ui-chart-rssi-receiver',
  templateUrl: './chart-rssi-receiver.component.html',
  imports: [HighchartsChartComponent, MatProgressBar, ChartFilterComponent],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [DateFormatPipe]
})
export class ChartRssiReceiverComponent implements OnDestroy {
  id = input<string>();

  readonly CHART_PERIOD_CONSTANTS = {
    DEFAULT_DAYS: 14,
    CHART_COLOR: '#375F9B'
  } as const;
  readonly CHART_STYLE_CONSTANTS = {
    FONT_FAMILY: '"Lucida Grande", "Lucida Sans Unicode", Arial, Helvetica, sans-serif',
    FONT_SIZE: '12px'
  } as const;
  displayLoader = signal(false);
  dataRange = signal<{ start: string; end: string }>({
    start: '',
    end: ''
  });
  chartOptions = signal<Highcharts.Options>(this.createInitialChartOptions());
  private chart: Highcharts.Chart | null = null;
  private readonly chartService = inject(ChartService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly translateService = inject(TranslateService);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);
  private readonly injector = inject(Injector);

  constructor() {
    effect(
      () => {
        const dataRange = this.dataRange();
        const id = this.id();
        if (id && !isEmpty(dataRange?.start) && !isEmpty(dataRange?.end)) {
          this.displayLoader.set(true);
          this.chartService
            .getReceiverRssiPoints(id, moment(dataRange.start), moment(dataRange.end))
            .pipe(
              catchError(() => of([])),
              finalize(() => this.displayLoader.set(false)),
              takeUntilDestroyed(this.destroyRef)
            )
            .subscribe((points: ChartAxisPoint[]) => {
              this.renderGraph(points);
            });
        }
      },
      { injector: this.injector }
    );
  }

  onChartCreated(chart: Highcharts.Chart): void {
    this.chart = chart;
  }

  dataRangeChange(event: { start: string; end: string }): void {
    this.dataRange.set(event);
  }

  ngOnDestroy(): void {
    this.displayLoader.set(false);
    this.chart = null;
  }

  private createInitialChartOptions(): Highcharts.Options {
    return {
      colors: [this.CHART_PERIOD_CONSTANTS.CHART_COLOR],
      title: { text: '' },
      credits: { enabled: false },
      navigator: { enabled: false },
      rangeSelector: {
        enabled: false,
        inputEnabled: false
      },
      tooltip: {
        shared: true,
        split: false
      },
      scrollbar: { enabled: false },
      chart: {
        style: {
          fontFamily: this.CHART_STYLE_CONSTANTS.FONT_FAMILY,
          fontSize: this.CHART_STYLE_CONSTANTS.FONT_SIZE
        },
        zooming: {
          type: 'x',
          singleTouch: true
        }
      },
      yAxis: {
        title: {
          text: this.translateService?.instant('I18N_CHART.MESSAGE_COUNT')
        },
        labels: {
          format: `{value}`,
          style: {
            color: this.CHART_PERIOD_CONSTANTS.CHART_COLOR
          }
        }
      },
      xAxis: {},
      plotOptions: {
        series: {
          borderRadius: '25%'
        } as never
      },
      legend: { enabled: false },
      series: [],
      exporting: {
        buttons: {
          contextButton: {
            menuItems: ['viewFullscreen', 'separator', 'downloadCSV', 'downloadXLS']
          }
        }
      }
    };
  }

  private reflow(): void {
    this.chart?.reflow();
  }

  private renderGraph(points: ChartAxisPoint[]): void {
    const sortedSeries = orderBy(points, ['x'], ['asc']);
    const rssiCategories = Array.from(new Map(sortedSeries.map((point) => [point.x, point.x])).values());
    const columnData = rssiCategories.map((rssi) => {
      const messageCount = sortedSeries.filter((point) => point.x === rssi).reduce((sum, point) => sum + point.y, 0);
      return {
        name: `RSSI ${rssi}`,
        y: messageCount
      };
    });
    const series = [
      {
        type: 'column',
        name: this.translateService.instant('I18N_CHART.MESSAGE_COUNT'),
        data: columnData
      }
    ] as Highcharts.SeriesColumnOptions[];

    this.chartOptions.update((chartOptions) => ({
      ...chartOptions,
      series,
      xAxis: {
        categories: rssiCategories,
        title: {
          text: 'RSSI'
        }
      } as never
    }));

    this.reflow();
    this.cdr.markForCheck();
  }
}
